import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../src/hooks/useAuth.js";
import { useDeleteAccountMutation } from "../../../src/store/api/authApi.js";
import cs from "./DangerModal.module.css";

const CONFIRM_TEXT = "DELETE";

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deleteAccount, { isLoading }] = useDeleteAccountMutation();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = warnings, 2 = confirm
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleConfirm = async () => {
    if (!password) {
      toast.error("Please enter your password.");
      return;
    }
    if (confirmText !== CONFIRM_TEXT) {
      toast.error(`Please type ${CONFIRM_TEXT} exactly to confirm.`);
      return;
    }

    try {
      await deleteAccount({ password, confirmText }).unwrap();
      toast.success("Your account has been permanently deleted.");
      logout();
      navigate("/");
    } catch (err) {
      const message =
        err?.data?.message ||
        Object.values(err?.data || {}).flat().join(" ") ||
        "Failed to delete account.";
      toast.error(message);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStep(1);
    setPassword("");
    setConfirmText("");
    setShowPass(false);
  };

  return (
    <>
      {/* ── Row trigger ── */}
      <div className={cs.dangerRow}>
        <div className={cs.dangerRowLeft}>
          <span className={cs.dangerIcon} style={{ background: "#fef2f2", color: "#c0392b" }}>
            <TrashIcon />
          </span>
          <div>
            <div className={cs.dangerRowTitle} style={{ color: "#c0392b" }}>
              Delete Account
            </div>
            <div className={cs.dangerRowSub}>
              Permanently remove your account and all associated data. This cannot be undone.
            </div>
          </div>
        </div>
        <button
          className={cs.btnDanger}
          onClick={() => setOpen(true)}
        >
          Delete
        </button>
      </div>

      {/* ── Modal ── */}
      {open && (
        <div className={cs.overlay} onClick={handleClose}>
          <div className={cs.modal} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className={cs.modalHeader} style={{ borderColor: "#e74c3c" }}>
              <span className={cs.modalIconWrap} style={{ background: "#fef2f2", color: "#c0392b" }}>
                <TrashIcon />
              </span>
              <div>
                <h3 className={cs.modalTitle}>Delete your account permanently?</h3>
                <p className={cs.modalSubtitle}>This action is irreversible and cannot be undone.</p>
              </div>
              <button className={cs.closeBtn} onClick={handleClose} aria-label="Close">✕</button>
            </div>

            {/* ── Step 1: Warnings ── */}
            {step === 1 && (
              <>
                <div className={cs.infoBox} style={{ background: "#fef2f2", borderColor: "#f5b7b1" }}>
                  <p className={cs.infoTitle} style={{ color: "#c0392b" }}>
                    ⚠️ Everything will be permanently deleted:
                  </p>
                  <ul className={cs.infoList} style={{ color: "#7b241c" }}>
                    <li>Your profile and all personal information</li>
                    <li>All posts, comments and likes</li>
                    <li>All chat messages and conversations</li>
                    <li>All followers and following relationships</li>
                    <li>Your account cannot be recovered after deletion</li>
                  </ul>
                </div>

                <p className={cs.stepNote}>
                  If you want to take a break instead,{" "}
                  <strong>deactivating your account</strong> keeps all your data safe.
                </p>

                <div className={cs.modalActions}>
                  <button className={cs.btnCancel} onClick={handleClose}>
                    Keep Account
                  </button>
                  <button
                    className={cs.btnDanger}
                    onClick={() => setStep(2)}
                  >
                    I understand, continue
                  </button>
                </div>
              </>
            )}

            {/* ── Step 2: Confirmation ── */}
            {step === 2 && (
              <>
                <div className={cs.fieldGroup}>
                  <label className={cs.fieldLabel}>
                    Type <strong>{CONFIRM_TEXT}</strong> to confirm
                  </label>
                  <input
                    className={`${cs.input} ${
                      confirmText && confirmText !== CONFIRM_TEXT ? cs.inputError : ""
                    } ${confirmText === CONFIRM_TEXT ? cs.inputSuccess : ""}`}
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                    placeholder={CONFIRM_TEXT}
                    autoFocus
                  />
                </div>

                <div className={cs.fieldGroup}>
                  <label className={cs.fieldLabel}>Enter your password</label>
                  <div className={cs.inputWrap}>
                    <input
                      className={cs.input}
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your current password"
                    />
                    <button
                      type="button"
                      className={cs.eyeBtn}
                      onClick={() => setShowPass((p) => !p)}
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className={cs.modalActions}>
                  <button className={cs.btnCancel} onClick={() => setStep(1)}>
                    ← Back
                  </button>
                  <button
                    className={cs.btnDanger}
                    onClick={handleConfirm}
                    disabled={isLoading || confirmText !== CONFIRM_TEXT || !password}
                  >
                    {isLoading ? "Deleting…" : "Delete My Account"}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}